import React, { useState } from 'react';
import { RefreshCallback } from '../../BlockFetcher';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { FormContainerModifier, FormUtils, useFormContext } from '../../../components/molecules/Form';
import IForm from '../../../types/IForm';
import { InteractionStatus } from '../../../types/InteractionStatus';
import Form from '../../../components/molecules/Form/Form';
import { ImageUpload } from './FormImageUpload';
import { PreviewSuspense } from './PreviewSuspense';
import { PreviewWithCropper } from './PreviewWithCropper';
import FormsRow, { FormsRowModifier } from '../../../components/molecules/Form/components/FormsRow/FormsRow';
import Btn from '../../../components/molecules/Btn/Btn';
import LiveRegion from '../../../components/atoms/LiveRegion/LiveRegion';
import { SelectedImagePreview } from './SelectedImagePreview';
import { Image as ImageType, ImageTypes } from '../../../types/api/ImageType';
import { BtnModifier } from '../../../components/molecules/Btn';
import { ResponsiveType } from '../../../components/molecules/Image';
import { ImagePreview } from './ImagePreview';
import { ImageUploadHeadline } from './ImageUploadHeadline';
import { ImageUploadParams } from './ImageUploadParams';
import { ImageUploadDescription } from './ImageUploadDescription';
import { isValid, ValidSelectedImage } from '../../FileUpload/utils/validateImage';
import { useSingleImageUpload } from './useSingleImageUpload';
import { ContentParagraph } from '../ContentParagraph';
import ImageTranslation from '../../../translation/de/components/Image-de';
import { UserDefinedAspectRatioSelect } from './UserDefinedAspectRatioSelect';
import { Strong } from '../../../components/atoms/Strong/Strong';

type ExistingAltTextKeys = keyof typeof ImageTranslation.alternativeText;
type DerivedAltTextKeys = Extract<ExistingAltTextKeys, ImageTypes>;
const getPreviewAltTextKey = (imageType: ImageTypes) => {
  return Object.keys(ImageTranslation.alternativeText).find(key => imageType === key) as DerivedAltTextKeys | undefined;
};

type ImageUploadProps = ImageUploadFormTemplateProps & {
  form: IForm;
  formId: string;
  refresh: RefreshCallback;
};

// see AspectRatioOption.java
export type AspectRatioOption = {
  /**
   * Aspect ratio name, e.g. 'banner'.
   */
  name: string;

  /**
   * The 16 in a 16:9 ratio.
   */
  numerator: number;

  /**
   * The 9 in a 16:9 ratio.
   */
  denominator: number;

  /**
   * The minimum image height (px) for this aspectRatio to be usable (considering the minWidth).
   */
  minHeight: number;
};

type UserSelectedAspectRatioState = {
  /**
   * The subset of [props.]userSelectableAspectRatios which are suitable for the currently cropped image.
   */
  suitableAspectRatios?: AspectRatioOption[];

  /**
   * The item of `suitableAspectRatios` which is currently selected for the cropping area.
   */
  selectedAspectRatio?: AspectRatioOption;
};

type ImageUploadFormTemplateProps = {
  imageDescriptionName: string;
  uuid: string;
  cropping: boolean;
  responsiveType?: ResponsiveType;
  viewportSwitch?: boolean;
  initialImage?: ImageType;
  additionalDescription?: string;
  /**
   * Optional list of aspect ratios that the user may choose from (when no hard constraints are enforced by the BE).
   * Displayed as radio buttons which set the suitable cropping rectangle on the croppie.
   */
  userSelectableAspectRatios?: AspectRatioOption[];
  selectedAspectRatio?: string;
};

export function ImageUploadFormTemplate({
  uuid,
  cropping,
  initialImage,
  viewportSwitch,
  responsiveType,
  imageDescriptionName,
  additionalDescription,
  userSelectableAspectRatios,
}: ImageUploadFormTemplateProps) {
  const { inputs, status, getStatus, hasChanges } = useFormContext();
  const {
    inputProps,
    labelProps,
    constraints,
    selectedPreview,
    suspendPreviewProps,
    imageUploadParamsProps,
    imageInModel,
  } = useSingleImageUpload(uuid);
  const { t } = useSonTranslation('ImageUpload');
  const statusMessages = {
    [InteractionStatus.SUCCESS]: FormUtils.createStatusMessage(t('uploadSuccess', { imageDescriptionName })),
    [InteractionStatus.FAIL]: FormUtils.createStatusMessage(
      t('uploadFail.general', {
        imageDescriptionName,
      }),
    ),
    invalidFile: FormUtils.createStatusMessage(t('uploadFail.invalidFile')),
    invalidColorSpace: FormUtils.createStatusMessage(t('uploadFail.invalidColorSpace')),
    invalidOrientation: FormUtils.createStatusMessage(t('uploadFail.invalidOrientation')),
    invalidImageDimension: FormUtils.createStatusMessage(
      t('uploadFail.invalidImageDimension', {
        maxWidth: constraints?.maxWidth,
        maxHeight: constraints?.maxHeight,
      }),
    ),
    [InteractionStatus.SENDING]: FormUtils.createStatusMessage(
      <>
        {t('uploadInProgress')}
        <Strong> {t(`phrases:pleaseWait`)}</Strong>
      </>,
    ),
  };
  // @ts-expect-error -- form typing sucks
  const imageType = inputs.imageuploadparams.params?.imageType?.value as ImageTypes;
  const altTextTranslationKey = getPreviewAltTextKey(imageType);
  const { aspectRatio: _, ...constraintsWithoutAspectRatio } = constraints;
  const [userSelectedAspectRatioState, setUserSelectedAspectRatioState] = useState<UserSelectedAspectRatioState>({});

  const validateSelectedImage = (img: HTMLImageElement) => {
    // determine available aspect ratios by the minimum image height
    const suitableAspectRatios =
      userSelectableAspectRatios?.filter(aspectRatio => img.naturalHeight >= aspectRatio.minHeight) || [];

    // keep the previous aspect ratio selection if still available or default to a new aspect ratio
    const selectedAspectRatio =
      (userSelectedAspectRatioState.selectedAspectRatio &&
        suitableAspectRatios.find(it => it.name === userSelectedAspectRatioState.selectedAspectRatio?.name)) ||
        suitableAspectRatios[0];

    setUserSelectedAspectRatioState({ suitableAspectRatios, selectedAspectRatio });
    return [];
  };

  const getPreview = (validPreview: ValidSelectedImage) =>
    cropping
      ? (
          <PreviewWithCropper
            selectedPreview={validPreview}
            viewportSwitch={viewportSwitch}
            fixedAspectRatio={userSelectedAspectRatioState.selectedAspectRatio || userSelectableAspectRatios?.[0]}
            validationCallback={validateSelectedImage}
          />
        )
      : (
          <SelectedImagePreview
            selectedPreview={validPreview}
            responsiveType={responsiveType}
            filename={validPreview.file.name}
            customAltText={altTextTranslationKey && t(`Image:alternativeText.${altTextTranslationKey}`)}
            imageType={imageType}
          />
        );

  const handleChangedAspectRatioSelection = (selectedAspectRatioName: string) =>
    setUserSelectedAspectRatioState({
      ...userSelectedAspectRatioState,
      selectedAspectRatio: userSelectedAspectRatioState.suitableAspectRatios?.find(
        it => it.name === selectedAspectRatioName,
      ),
    });

  return (
    <>
      <ImageUploadHeadline />
      <ImageUploadDescription constraints={imageType === 'profileImage' ? constraintsWithoutAspectRatio : constraints}>
        {additionalDescription && <ContentParagraph>{additionalDescription}</ContentParagraph>}
      </ImageUploadDescription>
      <ImageUpload inputProps={inputProps} labelProps={labelProps}>
        <PreviewSuspense {...suspendPreviewProps}>
          {isValid(selectedPreview)
            ? (
                getPreview(selectedPreview)
              )
            : (
                <ImagePreview image={initialImage} responsiveType={responsiveType} />
              )}
        </PreviewSuspense>
        {cropping && isValid(selectedPreview) && userSelectableAspectRatios && (
          <UserDefinedAspectRatioSelect
            allSelectableAspectRatios={userSelectableAspectRatios}
            suitableSelectableAspectRatios={userSelectedAspectRatioState.suitableAspectRatios || []}
            activeUserSelectedAspectRatio={
              userSelectedAspectRatioState.selectedAspectRatio?.name || userSelectableAspectRatios[0].name
            }
            onChangedUserSelectedAspectRatio={handleChangedAspectRatioSelection}
          />
        )}
        <ImageUploadParams {...imageUploadParamsProps} hasInitialImage={!!initialImage} />
      </ImageUpload>
      <FormsRow modifier={[FormsRowModifier.RIGHT, FormsRowModifier.NO_MARGIN]}>
        <Btn
          type="submit"
          modifier={[BtnModifier.SINGLE_ACTION_YES, BtnModifier.TRANSPARENT]}
          text={t('save')}
          ariaDisabled={
            status === InteractionStatus.SUCCESS ||
            status === InteractionStatus.SENDING ||
            !hasChanges() ||
            !imageInModel
          }
        />
      </FormsRow>
      <LiveRegion invisible={false} htmlTag="div">
        {getStatus(statusMessages, status)}
      </LiveRegion>
    </>
  );
}

export function ImageUploadForm({ form, refresh, formId, ...templateProps }: ImageUploadProps) {
  return (
    <div className="image-upload">
      <Form
        id={formId}
        onSuccess={() =>
          setTimeout(() => {
            refresh();
          }, 2000)}
        model={form}
        containerModifier={FormContainerModifier.NO_PADDING}
      >
        <ImageUploadFormTemplate {...templateProps} />
      </Form>
    </div>
  );
}
